import Link from "next/link";
import { Button } from "@/components/ui/button";
import { InfoHint } from "@/components/ui/info-hint";
import {
  UsersIcon,
  OnlineIcon,
  ServerIcon,
  CopyIcon,
  CheckIcon,
  SettingsIcon,
  InfoIcon,
} from "@/components/icons";

interface OrganizationHeaderProps {
  name: string;
  description?: string;
  memberCount: number;
  onlineCount: number;
  slug: string;
  serverName?: string;
  serverAddress: string | null;
  isMember: boolean;
  isAdmin: boolean;
  copiedIp: boolean;
  joining: boolean;
  onJoin: () => void;
  onCopyServerIp: () => void;
}

export function OrganizationHeader({
  name,
  description,
  memberCount,
  onlineCount,
  slug,
  serverName,
  serverAddress,
  isMember,
  isAdmin,
  copiedIp,
  joining,
  onJoin,
  onCopyServerIp,
}: OrganizationHeaderProps) {
  return (
    <header className="border-b border-border bg-foreground/5">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{name}</h1>
            {description && <p className="mt-1 text-muted">{description}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-1 text-muted">
                <UsersIcon className="h-4 w-4" />
                {memberCount} members
              </span>
              <span className="inline-flex items-center gap-1 text-primary">
                <OnlineIcon className="h-4 w-4" />
                {onlineCount} online
              </span>
              {(serverAddress || serverName) && (
                <span className="inline-flex items-center gap-1 text-muted">
                  <InfoIcon className="h-4 w-4" />
                  Live server details
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3">
            {(serverAddress || serverName) && (
              <div className="flex items-stretch gap-2 rounded-xl border border-border bg-background/80 p-2 shadow-sm backdrop-blur-sm">
                <div className="min-w-[210px] rounded-lg border border-border/70 bg-foreground/[0.02] px-3 py-2">
                  <div className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted">
                    Server Name
                    <InfoHint text="This is the in-game server identity linked to this organization." />
                  </div>
                  <p className="mt-1 truncate text-sm font-semibold text-foreground">
                    {serverName || "Not set"}
                  </p>
                </div>

                {serverAddress && (
                  <button
                    type="button"
                    onClick={onCopyServerIp}
                    className="group min-w-[210px] cursor-pointer rounded-lg border border-border/70 bg-foreground/[0.02] px-3 py-2 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/50 hover:bg-primary/[0.06]"
                    aria-label={`Copy server address ${serverAddress}`}
                  >
                    <div className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted">
                      Server Address
                      <InfoHint text="Click to copy the connect address for this server." />
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-3">
                      <div className="inline-flex items-center gap-2">
                        <ServerIcon className="h-4 w-4 text-muted transition-colors group-hover:text-primary" />
                        <code className="text-sm font-semibold text-foreground">
                          {serverAddress}
                        </code>
                      </div>
                      {copiedIp ? (
                        <CheckIcon className="h-4 w-4 text-primary transition-transform duration-200 group-hover:scale-110" />
                      ) : (
                        <CopyIcon className="h-4 w-4 text-muted transition-all duration-200 group-hover:-translate-y-0.5 group-hover:scale-110 group-hover:text-primary" />
                      )}
                    </div>
                  </button>
                )}
              </div>
            )}
            {!isMember && (
              <Button onClick={onJoin} disabled={joining}>
                {joining ? "Joining..." : "Join Organization"}
              </Button>
            )}
            {isAdmin && (
              <Link href={`/org/${slug}/settings`}>
                <Button variant="outline" size="icon">
                  <SettingsIcon className="h-4 w-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
